import {Component, CUSTOM_ELEMENTS_SCHEMA, inject, model, resource, signal, viewChild} from '@angular/core';
import {WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {WjGridSearchModule} from '@mescius/wijmo.angular2.grid.search';
import {ColumnDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {DataType, NotifyCollectionChangedEventArgs, INotifyCollectionChanged} from '@mescius/wijmo';
import {CellRangeEventArgs} from '@mescius/wijmo.grid';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {WjInputModule} from '@mescius/wijmo.angular2.input';
import {AdminUserPermissionsComponent} from '../../components/admin-user-permissions/admin-user-permissions.component';
import {AppRoleAndPermissions} from '../../security/app-role-and-permissions';
import {Alerts, DialogButton} from '../../lib/alerts';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {UserProfileApi, UserProfileData} from '../../../api-client-kt/src';

const userColumns: ColumnDeclaration[] = [
    {header:"User ID", binding:"userId",dataType:DataType.String, visible: false, width: 250},
    {header:"Name", binding:"userName",dataType:DataType.String, visible: true, width: '*'},
    {header:"Full Name", binding:"fullName",dataType:DataType.String, visible: true, width: 250},
    {header:"Enabled", binding:"enabled",dataType:DataType.Boolean, visible: true, width: 80, align: 'center'},
    {header:"Expires", binding:"expirationDate",dataType: DataType.Date, format: 'yyyy-MM-dd', visible: true, width: 120, align: 'center' },
];

@Component({
    selector: 'app-admin-users',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        WjGridFilterModule,
        WjGridModule,
        WjGridSearchModule,
        FormsModule,
        ReactiveFormsModule,
        WjInputModule,
        AdminUserPermissionsComponent
    ],
    templateUrl: './admin-users.component.html',
    styleUrl: './admin-users.component.scss',
})
export class AdminUsersComponent {

    protected appRoleAndPermissions = model(new AppRoleAndPermissions([]));
    protected userProfile = signal<UserProfileData|undefined>(undefined);

    private readonly userRolePermissionAdminControl = viewChild<AdminUserPermissionsComponent>(AdminUserPermissionsComponent);
    protected readonly pfData = inject(PortfolioDataContext);

    protected readonly Wijmo = Wijmo;

    protected readonly visibleRowCount = signal(0);
    protected readonly userProfileApi = inject(UserProfileApi)

    protected readonly userProfiles: UserProfileData[] = [];
    protected readonly userProfilesGrid = viewChild<WjFlexGrid>('grid');

    protected async onGridInitialized(grid: WjFlexGrid) {
        WjGridUtils.addColumns(grid, userColumns, true);
        grid.refreshed.addHandler(() => this.visibleRowCount.set(grid.rows.length));
        await this.loadUsers();
        WjGridUtils.unselectAll(grid);

        // this handler ensures that the user profile display / matrix is updated
        // when a user is indirectly selected, for example,
        // by pasting their full name in the search bar
        grid.collectionView.collectionChanged.addHandler((cv: INotifyCollectionChanged, args: NotifyCollectionChangedEventArgs) => {
            const row = grid.selectedItems?.[0] as UserProfileData | undefined;
            this.activateUserProfile(row);
        });
    }

    private async loadUsers() {
        const profiles = await this.userProfileApi.getUserProfiles();
        this.userProfiles.splice(0, this.userProfiles.length, ...profiles);
        this.userProfilesGrid()?.collectionView?.refresh();
    }

    protected onSelectedUserChanged(event: CellRangeEventArgs, grid: WjFlexGrid) {
        const row = event.getRow();
        this.activateUserProfile(row?.dataItem as UserProfileData);
    }

    private activateUserProfile(userProfile: UserProfileData | undefined) {
        if (userProfile != null) {
            this.userProfile.set(userProfile);
            this.appRoleAndPermissions.set(AppRoleAndPermissions.fromUserProfileData(userProfile));
        } else {
            this.userProfile.set(undefined);
        }
    }

    protected async onCreateUser() {
        const result = await Alerts.alertPrompt({ body: 'Enter the email of the new user' });
        const userName = result?.trim()?.toLowerCase();
        if (userName) {
            const fullName = await Alerts.alertPrompt({ body: 'Enter the full name of the new user' });
            const expirationDate = new Date();
            expirationDate.setFullYear(new Date().getFullYear() + 5);
            const userProfileData: UserProfileData =  { userName, fullName, roles: new Set(), permissions: new Set(), enabled: true, expirationDate };
            await this.userProfileApi.createUserProfile({ userProfileData });
            await this.loadUsers();
        }
    }

    protected async onToggleEnable() {
        const userProfile = this.userProfile();
        const userProfileId = userProfile?.userId;
        if (userProfile && userProfileId) {
            userProfile.enabled = !userProfile.enabled;
            await this.userProfileApi.updateUserProfile({ id: userProfileId, userProfileData: userProfile });
            await this.loadUsers();
        }
    }

    protected async onRemoveUser() {
        const userProfileId = this.userProfile()?.userId;
        const userName = this.userProfile()?.userName;
        if (userProfileId) {
            const btn = await Alerts.alertPopup({
                body: `Are you sure you want to delete user ${userName}?\n\nThis action cannot be undone.`,
                buttons: [DialogButton.Yes, DialogButton.No]
            });
            if (btn === DialogButton.Yes) {
                await this.userProfileApi.deleteUserProfile({id: userProfileId});
                await this.loadUsers();
            }
        }
    }

    protected async savePermissions(event: AppRoleAndPermissions) {
        const userProfileData = this.userProfile();
        const id = userProfileData?.userId;
        if (id && userProfileData) {
            userProfileData.roles = new Set(event.roles);
            const permissionsAsList = Object.keys(event.permissions).flatMap(lobKey => {
                return Object.keys(event.permissions[lobKey]).map(officeKey => {
                    return ({lobKey, officeKey, allow: event.permissions[lobKey][officeKey]});
                });
            });
            userProfileData.permissions = new Set(permissionsAsList);
            await this.userProfileApi.updateUserProfile({ id, userProfileData });
            await this.loadUsers();
        }

    }

    protected onSearchFieldKeyDown(event: KeyboardEvent) {
        //console.log(event);
    }
}
